import { Dispatch, Fragment, SetStateAction } from 'react';
import { Typography, Link, Grid } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Chart from 'react-apexcharts';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal/hooks';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import AssessmentScoreLegend from 'components/common/AssessmentScoreLegend';
import StudentScoreModal from 'components/modules/ClassReports/Components/StudentScoreModal';
import {
  AssessmentReport,
  AssessmentType,
  BenchmarkAssessment,
  Checklist,
  Domain,
  Remediation,
  Student,
  UnitAssessment,
  UnitChecklist,
} from 'types';
import RecommendationsModal from 'components/modules/Class/components/RecommendationsModal';
import { FormattedAssessmentOutcomes } from 'types/formatted-assessment-outcomes';
import { Link as RouterLink } from 'react-router-dom';
import { formatReports } from './class-reports-util';
import { getCurriculumBenchmarkAssessment } from 'util/assessments-util';
import { getCurriculumBenchmarkChecklists } from 'util/checklists-util';

const chartOptions = {
  chart: {
    stacked: true,
    stackType: '100%' as const,
    selection: { enabled: false },
    toolbar: {
      show: false,
    },
  },
  plotOptions: {
    bar: {
      horizontal: true,
    },
  },
  legend: {
    show: false,
  },
  xaxis: {
    labels: { show: false },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    show: false,
  },
  grid: {
    show: false,
  },
  dataLabels: {
    enabled: false,
  },
  noData: {
    text: 'data',
    style: { color: '#fff' },
  },
  tooltip: {
    enabled: true,
    x: {
      show: false,
    },
  },
  states: {
    active: {
      filter: {
        type: 'none',
        value: 0,
      },
    },
  },
  stroke: {
    width: 3,
    colors: ['#fff'],
  },
};

const t = (key) => translate('ui', key);

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
      '& .MuiGrid-item': {
        paddingTop: '0 !important',
        paddingLeft: '0 !important',
        minHeight: '50px',
      },
    },
    chartTitle: {
      '&.MuiGrid-root': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      },
      '& p': {
        textAlign: 'right',
      },
    },
    recommendations: {
      '&.MuiGrid-root': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      },
      '& .MuiLink-root': {
        color: 'black',
        textDecoration: 'none',
        fontFamily: 'HWT Futura',
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    link: {
      '&.MuiLink-root': {
        textAlign: 'center',
        textDecoration: 'none',
        fontFamily: 'HWT Futura',
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
        '& p': {
          marginTop: '3%',
        },
      },
    },
  };
});

const AssessmentBarCharts = (props: {
  unit?: string;
  benchmark?: string;
  domain: string;
  allDomains: Domain[];
  reports: AssessmentReport[];
  students: Student[];
  checklists: Checklist[];
  classroomId: number;
  curriculumId: number;
  setStateCallback: Dispatch<SetStateAction<any>>;
}) => {
  const [, { openModal }] = useModal();
  const classes = useStyles();

  const formattedReports = formatReports(
    props.curriculumId,
    props.allDomains,
    props.reports,
    props.domain,
    props.unit,
    props.benchmark
  );

  const benchmarkChecklists = getCurriculumBenchmarkChecklists(
    props.checklists,
    props.curriculumId
  );

  const displayRecommendationModal = (remediations: Remediation[]) => {
    openModal({
      body: <RecommendationsModal remediations={remediations} />,
    });
  };

  const displayStudentScoreModal = (outcomes: FormattedAssessmentOutcomes, students: Student[]) => {
    openModal({
      body: <StudentScoreModal outcomes={outcomes} students={students} />,
    });
  };

  const findChecklist = (assessmentId?: number) => {
    if (!assessmentId) return undefined;

    const assessment = props.reports.find((v) => v.assessmentId === assessmentId)!.assessment;

    if (assessment.type === AssessmentType.Benchmark) {
      const benchmarkAssessment = getCurriculumBenchmarkAssessment(
        assessment as BenchmarkAssessment,
        props.curriculumId
      );

      return benchmarkChecklists.find((v) => v.period === benchmarkAssessment.period);
    } else {
      return props.checklists.find(
        (v) => (v as UnitChecklist).unitId === (assessment as UnitAssessment).unitId
      );
    }
  };

  const displayChart = (data: FormattedAssessmentOutcomes, students: Student[]) => {
    const series = makeSeries(data);
    const hoverFilter =
      series.length > 1
        ? { filter: { type: 'lighten', value: 0.15 } }
        : { filter: { type: 'none', value: 0 } };
    const checklist = findChecklist(data.assessmentId);
    return series.length === 1 && checklist ? (
      <Link
        component={RouterLink}
        to={`/checklist/${checklist.id}?classId=${props.classroomId}&curriculumId=${props.curriculumId}`}
        className={classes.link}
      >
        <Typography variant="body1">{t('report.viaChecklist')}</Typography>
      </Link>
    ) : (
      <Chart
        options={{
          ...chartOptions,
          tooltip: { ...chartOptions.tooltip, enabled: series.length > 1 },
          states: { ...chartOptions.states, hover: hoverFilter },
        }}
        series={series}
        type="bar"
        width="100%"
        height="70%"
        onClick={() => {
          if (series.length > 1) {
            displayStudentScoreModal(data, students);
          }
        }}
      />
    );
  };

  const makeSeries = (data: FormattedAssessmentOutcomes) => {
    if (data.meets.length + data.growing.length + data.early.length + data.notTested.length === 0) {
      return [
        {
          data: [{ y: 1, x: 1, fillColor: '#b0b0b0' }],
        },
      ];
    }
    return [
      {
        name: t('meetsExpectations'),
        data: [{ y: data.meets.length, x: 1, fillColor: '#098e51' }],
      },
      {
        name: t('growing'),
        data: [{ y: data.growing.length, x: 1, fillColor: '#6c820c' }],
      },
      {
        name: t('earlyEmerging'),
        data: [{ y: data.early.length, x: 1, fillColor: '#e86825' }],
      },
      {
        name: t('notTested'),
        data: [{ y: data.notTested.length, x: 1, fillColor: '#0c72b7' }],
      },
    ];
  };

  return (
    <>
      <AssessmentScoreLegend />
      {formattedReports.map((report) => {
        return (
          <Fragment key={`chart-for-${report.label}-${report.sectionId}`}>
            <Grid container item xs={10} spacing={2} className={classes.container}>
              <Grid item xs={3} className={classes.chartTitle}>
                <Typography
                  onClick={() => {
                    if (report.id) {
                      return props.setStateCallback(report.id);
                    }
                  }}
                >
                  {report.label}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                {displayChart(report, props.students)}
              </Grid>
              <Grid item xs={3} className={classes.recommendations}>
                {report?.remediations && report.remediations.length > 0 && (
                  <Link onClick={() => displayRecommendationModal(report.remediations!)}>
                    <Typography variant="body1" component="p">
                      {t('class.recommendations')}
                    </Typography>
                  </Link>
                )}
              </Grid>
            </Grid>
          </Fragment>
        );
      })}
    </>
  );
};

export default AssessmentBarCharts;
